import * as THREE from "three";
import { CLASSIFICATION_COLORS, PREDEFINED_COLORS } from "./colors";
import pointVert from "./shaders/point.vert";

export const COLOR_MODE = {
    rgb: { key: 1, name: "RGB" },
    classification: { key: 2, name: "Classification" },
    intensity: { key: 3, name: "Intensity" },
    sourceID: { key: 4, name: "Point Source ID" },
    metaIndex: { key: 5, name: "File" },
    dataset: { key: 6, name: "Dataset" },
    extra: { key: 7, name: "Extra Data" },
    elevation: { key: 9, name: "Elevation" },
    object: { key: 10, name: "Object" },
    hag: { key: 11, name: "Height Above Ground" },
} as const;

const COLOR_MODE_PICK = 8;
const CLASS_FILTER_BYTES = 8;
const MAX_OBJECT_ID_FILTER = 256;
const MAX_CLIP_POLYGON = 256;

export type ColorMode = keyof typeof COLOR_MODE;

export function getColorModeById(id: number) {
    const hit = Object.entries(COLOR_MODE).find(([, value]) => value.key === id);
    if (hit) {
        return hit[0] as ColorMode;
    } else {
        console.error(`Unknown color mode ID: ${id}. Defaulting to RGB.`);
        return COLOR_MODE.rgb;
    }
}

export type CommonMaterialOptions = {
    zRange: [number, number];
    elevationRange: [number, number];
    hagRange: [number, number];
    hagFilterRange: [number, number] | null;
    pointSize: number;
    filteredPoseIDs: Set<number>;
    objectIdMask: number[] | null;
    colorMode: ColorMode;
    classificationMask: number[] | null;
    split: number;
    zOffset: number;
    clipPolygon: [number, number][] | null;
};

export class CustomPointMaterial extends THREE.ShaderMaterial {
    constructor(options: { datasetColor: [number, number, number]; pick?: { nodeIndex: number } }) {
        super({
            glslVersion: "300 es",
            clipping: true,
            transparent: false,
            uniforms: {
                pointSize: { value: 4.0 },
                colorMode: { value: options.pick ? COLOR_MODE_PICK : COLOR_MODE["rgb"].key },
                classificationMask: { value: new Uint32Array(CLASS_FILTER_BYTES).fill(0xffff_ffff) },
                colorRamp: { value: PREDEFINED_COLORS.flatMap((c) => c.map((v) => v / 255)) },
                colorRampLength: { value: PREDEFINED_COLORS.length },
                classificationColors: { value: CLASSIFICATION_COLORS.flatMap((c) => c.map((v) => v / 255)) },
                classificationColorsLength: { value: CLASSIFICATION_COLORS.length },
                datasetColor: { value: options.datasetColor },
                intensityRange: { value: [0, 1] },
                zRange: { value: [-Infinity, Infinity] },
                elevationRange: { value: [0, 100] },
                hagRange: { value: [0, 0] },
                hagFilterRange: { value: [-Infinity, Infinity] },
                filteredPoseId: { value: new Uint32Array(256) },
                filteredPoseIdLength: { value: 0 },
                objectIdFilter: { value: new Int32Array(MAX_OBJECT_ID_FILTER) },
                objectIdFilterLength: { value: 0 },
                split: { value: 0.0 },
                nodeIndex: { value: options.pick?.nodeIndex ?? 0 },
                clipPolygon: { value: new Float32Array(MAX_CLIP_POLYGON * 2) },
                clipPolygonLength: { value: 0 },
            },
            vertexShader: pointVert,
            fragmentShader: `
                #include <common>
				#include <clipping_planes_pars_fragment>
				#include <logdepthbuf_pars_fragment>
				out vec4 FragColor;

				in vec4 vColor;
				flat in int vDiscard;

				void main() {

                    #include <clipping_planes_fragment>
                    #include <logdepthbuf_fragment>

					if (vDiscard == 1) {
						discard;
					}

					// make the points circular
					float u = 2.0 * gl_PointCoord.x - 1.0;
					float v = 2.0 * gl_PointCoord.y - 1.0;
					if (u * u + v * v > 1.0) {
						discard;
					}

					FragColor = vColor;
				}
			`,
        });
    }

    /** NOTE: Range should be given in raw point coordinates.
     * That means it should not have any possible offset applied.
     *
     * @param zRange [minZ, maxZ] range of z values to render, in raw point coordinates without any offset.
     */
    private setVisibleZRange(zRange: number[]) {
        this.uniforms.zRange.value = zRange;
    }

    copyVisibilitySettings(other: CustomPointMaterial) {
        this.uniforms.zRange.value = other.uniforms.zRange.value;
        this.uniforms.elevationRange.value = other.uniforms.elevationRange.value;
        this.uniforms.hagFilterRange.value = other.uniforms.hagFilterRange.value;
        this.uniforms.classificationMask.value = other.uniforms.classificationMask.value;
        this.uniforms.objectIdFilter.value = other.uniforms.objectIdFilter.value;
        this.uniforms.objectIdFilterLength.value = other.uniforms.objectIdFilterLength.value;
    }

    private setFilteredPoseIDs(ids: Set<number>) {
        this.uniforms.filteredPoseIdLength.value = ids.size;
        let i = 0;
        for (const id of ids) {
            this.uniforms.filteredPoseId.value[i++] = id;
        }
    }

    private setObjectIdMask(mask: number[] | null) {
        if (mask === null) {
            this.uniforms.objectIdFilterLength.value = 0;
            return;
        }
        const arr = this.uniforms.objectIdFilter.value as Int32Array;
        const n = Math.min(mask.length, MAX_OBJECT_ID_FILTER);
        for (let i = 0; i < n; i++) {
            arr[i] = mask[i];
        }
        this.uniforms.objectIdFilterLength.value = n;
    }

    update(options: CommonMaterialOptions) {
        this.setVisibleZRange(options.zRange.map((z) => z - options.zOffset));
        this.uniforms.elevationRange.value = options.elevationRange.map((z) => z - options.zOffset);
        this.uniforms.hagRange.value = options.hagRange;
        this.uniforms.hagFilterRange.value = options.hagFilterRange ?? [-Infinity, Infinity];
        this.uniforms.pointSize.value = options.pointSize;
        this.setFilteredPoseIDs(options.filteredPoseIDs);
        this.setObjectIdMask(options.objectIdMask);
        this.uniforms.colorMode.value = COLOR_MODE[options.colorMode].key;
        this.uniforms.split.value = options.split;
        this.updateClassificationMask(options.classificationMask);
        this.setClipPolygon(options.clipPolygon);
    }

    private setClipPolygon(polygon: [number, number][] | null) {
        if (!polygon) {
            this.uniforms.clipPolygonLength.value = 0;
            return;
        }

        // Too many vertices to represent faithfully: dim nothing rather than
        // clip against a truncated (malformed) ring.
        if (polygon.length > MAX_CLIP_POLYGON) {
            this.uniforms.clipPolygonLength.value = 0;
            return;
        }
        const arr = this.uniforms.clipPolygon.value as Float32Array;
        const n = polygon.length;
        for (let i = 0; i < n; i++) {
            arr[i * 2] = polygon[i][0];
            arr[i * 2 + 1] = polygon[i][1];
        }
        this.uniforms.clipPolygonLength.value = n;
    }

    private updateClassificationMask(newMask: number[] | null) {
        if (!newMask) {
            for (let i = 0; i < CLASS_FILTER_BYTES; i++) {
                this.uniforms.classificationMask.value[i] = 0xffff_ffff;
            }
            return;
        }

        for (let i = 0; i < CLASS_FILTER_BYTES; i++) {
            this.uniforms.classificationMask.value[i] = 0;
        }

        for (const c of newMask) {
            const arrayIndex = Math.floor(c / 32);
            const bitIndex = c % 32;
            this.uniforms.classificationMask.value[arrayIndex] |= 1 << bitIndex;
        }
    }
}
